import { InferGetStaticPropsType } from 'next';
import Head from 'next/head';
import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import { Article } from 'schema-dts';
import HnaviLogo from '@images/hnavi-logo.png';
import { useCanonical } from '@hooks';
import {
  AltTags,
  Author,
  BreadcrumbsData,
  ContactFormSection,
  Cta1,
  Date,
  HnaviFooter,
  HnaviLinksBlock,
  InPostPager,
  JsonLdBase,
  Layout2,
  LpLinkBlock,
  PostBody,
  PostHeadShareButtons,
  RelatedPostsBlock,
  SideContents,
  Tags,
} from '@components';
import {
  fixPostImageTag,
  fixUrlsInPosts,
  getMetaDescription,
  insertAllRelatedPosts,
  splitByInsertions,
} from '@helpers';
import {
  title,
  shortTitle,
  hnaviTitle,
  hnaviShortTitle,
  casestudyTypes,
  hnaviAreas,
  baseBaseUrl,
} from '@constants';
import { excludeNullUndefined } from '@utils';
import {
  altTagPath,
  basePath,
  casestudyListPath,
  categoryPath,
  hnaviCategoryPath,
  hnaviCorporationPath,
  hnaviSearchPath,
} from '@paths';
import { getStaticProps } from '@/src/pages/knowledge/blog/[slug]';
import type { Tag as BaseTag, GetAltTagsQuery } from '@/src/generated/graphql';

type Tag = Pick<BaseTag, 'name' | 'slug' | 'useSlugForHnaviSearch'>;

const getCasestudyType = (
  post: InferGetStaticPropsType<typeof getStaticProps>['post']
) => {
  const key = post?.hnavi?.hnaviReferenceKey;
  if (!key) {
    return null;
  }
  const name: string | undefined = (casestudyTypes as any)[key];
  if (!name) {
    return null;
  }
  return { key, name };
};

const makeHnaviTagBreadcrumbs = (tag: Tag) => {
  const areaCode =
    tag.useSlugForHnaviSearch === 'true' && tag.slug?.includes('-slash-')
      ? tag.slug.split('-slash-')[0]
      : undefined;

  const areaName =
    areaCode &&
    Object.values(hnaviAreas)
      .flat()
      .find((e) => e.code === areaCode)?.name;

  return [
    { text: '開発会社一覧', url: hnaviCorporationPath },
    ...(areaName ? [{ text: areaName, url: hnaviSearchPath(areaCode) }] : []),
    {
      text: tag.name,
      url: hnaviSearchPath(
        tag.useSlugForHnaviSearch === 'true'
          ? tag.slug || ''
          : encodeURIComponent(tag.name || '')
      ),
    },
  ];
};

const makeBreadcrumbs = (
  post: InferGetStaticPropsType<typeof getStaticProps>['post'],
  {
    hnaviContentCategories,
    previewAltTags,
    previewPrimaryAltTagId,
  }: {
    hnaviContentCategories: { [code: string]: string };
    previewAltTags:
      | NonNullable<
          NonNullable<NonNullable<GetAltTagsQuery['altTags']>['nodes']>[0]
        >[]
      | null;
    previewPrimaryAltTagId: string | null;
  }
): BreadcrumbsData => {
  const category =
    post?.categories?.edges?.find((edge) => edge?.isPrimary)?.node ||
    post?.categories?.edges?.find((edge) => edge?.node?.slug === 'interview')
      ?.node ||
    post?.categories?.edges?.[0]?.node;
  const casestudy = getCasestudyType(post);

  const base: BreadcrumbsData[0] =
    category?.slug === 'interview' ||
    post?.hnavi?.hnaviMainCategory ||
    post?.hnavi?.hnaviTag?.name
      ? {
          text: hnaviTitle,
          shortText: hnaviShortTitle,
          url: '/',
        }
      : {
          text: title,
          shortText: shortTitle,
          url: basePath,
        };

  const middle: BreadcrumbsData = casestudy
    ? [
        {
          text: casestudy.name,
          url: casestudyListPath(casestudy.key as any),
        },
      ]
    : post?.hnavi?.hnaviTag?.name
    ? makeHnaviTagBreadcrumbs(post?.hnavi?.hnaviTag)
    : post?.hnavi?.hnaviMainCategory
    ? [
        {
          text: hnaviContentCategories[post.hnavi.hnaviMainCategory],
          url: hnaviCategoryPath(post.hnavi.hnaviMainCategory),
        },
      ]
    : [
        {
          text: category?.name,
          url: categoryPath(category?.slug),
        },
      ];

  const primaryAltTag = previewAltTags
    ? previewAltTags.find(
        (e) => e.databaseId === Number(previewPrimaryAltTagId)
      )
    : post.altTags?.edges?.find((e) => e?.isPrimary)?.node;
  const primaryAltTagBreadcrumbs: BreadcrumbsData =
    primaryAltTag && primaryAltTag.slug
      ? [
          {
            text: primaryAltTag.name,
            url: altTagPath(primaryAltTag.slug),
          },
        ]
      : [];

  return [base, ...middle, ...primaryAltTagBreadcrumbs, { text: post?.title }];
};

const getPagedPostInfo = (
  postContent: string,
  currentPageNumber: number | null | undefined,
  currentPath: string
) => {
  const basePath = currentPath.replace(/\/[0-9]+\/$/, '');
  const paged = postContent?.split('<!--nextpage-->') || [];
  const isPaged = paged.length > 1;
  const maxPageNumber = paged.length;
  return {
    paged,
    isPaged,
    maxPageNumber,
    currentPageNumber: currentPageNumber || 1,
    currentPage: isPaged
      ? paged[
          currentPageNumber ? Math.min(currentPageNumber, maxPageNumber) - 1 : 0
        ]
      : postContent,
    pageHrefs: [
      basePath,
      ...Array(maxPageNumber - 1)
        .fill(0)
        .map((_, i) => `${basePath.replace(/\/$/, '')}/${i + 2}/`),
    ],
  };
};

const structuredData = (
  post: InferGetStaticPropsType<typeof getStaticProps>['post']
): Article => ({
  '@type': 'Article',
  headline: post.title || '',
  image: post.featuredImage?.node?.sourceUrl || '',
  datePublished: post.date || '',
  dateModified: post.modified || '',
  publisher: {
    '@type': 'Organization',
    name: '発注ナビ',
    url: baseBaseUrl,
    logo: `${baseBaseUrl}${HnaviLogo.src}`,
  },
  author:
    post.hnavi?.showAuthor && post.author?.node?.name
      ? [
          {
            '@type': 'Person',
            name: post.author.node.name,
          },
        ]
      : undefined,
});

export const PostPage: React.FC<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({
  allCategories,
  categoriesIncludesInterview,
  categoriesIncludesSales,
  category,
  hnaviContentCategories,
  hnaviOptions,
  pageInPost,
  popularPosts,
  post,
  preview,
  relatedCaseContentPosts,
  relatedTokushuPosts,
  sameCategoryPosts,
  sameHnaviCategoryPosts,
  previewAltTags,
  previewPrimaryAltTagId,
}) => {
  const router = useRouter();
  const canonical = useCanonical();

  if (!post || !post.content) {
    return <ErrorPage statusCode={404} />;
  }

  const tags = post.tags?.nodes?.filter(excludeNullUndefined) || [];
  const altTags =
    previewAltTags ||
    post.altTags?.edges?.map((e) => e?.node).filter(excludeNullUndefined) ||
    [];

  const isCasestudy = !!getCasestudyType(post);

  const pagedPostInfo = getPagedPostInfo(
    post.content,
    pageInPost,
    router.asPath
  );

  const useHnaviHeader =
    isCasestudy ||
    ['feature', 'cost'].includes(post.hnavi?.hnaviReferenceKey || '');

  const { s: postBodySrc, firstImageAttrs } = fixPostImageTag(
    fixUrlsInPosts(
      insertAllRelatedPosts(
        pagedPostInfo.isPaged
          ? pagedPostInfo.paged[
              pageInPost
                ? Math.min(pageInPost - 1, pagedPostInfo.maxPageNumber - 1)
                : 0
            ]
          : post.content,
        [post.hnaviRelatedPosts1, post.hnaviRelatedPosts2]
      )
    )
  );

  return (
    <div className={`post-page-slug-${encodeURIComponent(post.slug || '')}`}>
      {firstImageAttrs && (
        <Head>
          <link
            rel='preload'
            fetchPriority='high'
            as='image'
            imageSrcSet={firstImageAttrs.srcSet}
            imageSizes={firstImageAttrs.sizes}
            href={firstImageAttrs.src}
          />
        </Head>
      )}
      {preview && (
        <div className='fixed z-10 bg-white p-2 font-bold text-red'>
          これはプレビューです
        </div>
      )}
      <JsonLdBase data={structuredData(post)} />
      <Layout2
        useHnaviHeader={useHnaviHeader}
        categories={allCategories}
        hnaviContentCategories={hnaviContentCategories}
        seoData={{
          ...post.seo,
          canonical,
          metaDesc: post.seo?.metaDesc || getMetaDescription({ post }),
        }}
        breadcrumbs={makeBreadcrumbs(post, {
          hnaviContentCategories,
          previewAltTags,
          previewPrimaryAltTagId,
        })}
        sideBar={
          <SideContents
            fixedBanner={
              ['corporation-casestudy', 'sales'].includes(
                post.hnavi?.hnaviReferenceKey || ''
              )
                ? hnaviOptions.banners.developerFixed
                : hnaviOptions.banners.buyerFixed
            }
            stickyBanner={
              ['corporation-casestudy', 'sales'].includes(
                post.hnavi?.hnaviReferenceKey || ''
              )
                ? undefined
                : hnaviOptions.banners.sticky
            }
            offsetStickyBannerForStickyTitleBar={useHnaviHeader}
            postGroups={[
              {
                groupName: '人気記事',
                posts: popularPosts,
              },
              {
                groupName: '関連記事',
                posts: sameCategoryPosts,
              },
              {
                groupName: '関連特集',
                posts: relatedTokushuPosts,
              },
            ]}
          />
        }
        footer={
          <>
            <HnaviFooter />
            <ContactFormSection
              className='mt-10'
              category={post.hnavi?.hnaviMainCategory || undefined}
              pardotUrl={hnaviOptions.original?.hnaviFieldPardotPardotUrl}
            />
          </>
        }
      >
        <h1 className='mb-6 text-xl font-bold text-primary lg:text-3xl'>
          {post.title}
        </h1>
        <div className='mb-4 flex justify-between'>
          <Date dateString={post.date} />
          <PostHeadShareButtons post={post} path={router.asPath} />
        </div>
        {altTags.length > 0 && (
          <div className='mb-4'>
            <AltTags tags={altTags} />
          </div>
        )}
        <PostBody
          content={splitByInsertions({
            src: postBodySrc,
            post,
            tags:
              tags.length > 0
                ? () => (
                    <div className='my-4'>
                      <Tags tags={tags} />
                    </div>
                  )
                : null,
            pardotUrl: hnaviOptions.original?.hnaviFieldPardotPardotUrl,
          })}
        />
        {pagedPostInfo.isPaged && (
          <InPostPager
            links={pagedPostInfo.pageHrefs.map((href, i) => ({
              href,
              label: String(i + 1),
              isCurrent: i + 1 === pagedPostInfo.currentPageNumber,
            }))}
          />
        )}
        {tags.length > 0 && !/<!-- 関連リンク[0-9]+ -->/.test(post.content) && (
          <div className='my-4'>
            <Tags tags={tags} />
          </div>
        )}
        {post.hnavi?.showAuthor && <Author author={post.author} />}
        <RelatedPostsBlock
          posts={sameCategoryPosts?.slice(0, 3)}
          text={`${category?.name}の関連コンテンツ`}
        />
        <RelatedPostsBlock
          posts={relatedCaseContentPosts?.slice(0, 3)}
          text={`${category?.name}の関連事例コンテンツ`}
        />
        <RelatedPostsBlock
          posts={sameHnaviCategoryPosts?.slice(0, 3)}
          text={`${
            hnaviContentCategories[post.hnavi?.hnaviMainCategory || '']
          }の関連コンテンツ`}
        />
        <div className='lg:mb-8'>
          <LpLinkBlock />
        </div>
        <HnaviLinksBlock
          categoryCode={post.hnavi?.hnaviMainCategory}
          categoryName={
            hnaviContentCategories[post.hnavi?.hnaviMainCategory || '']
          }
        />
      </Layout2>
      {categoriesIncludesInterview || categoriesIncludesSales || <Cta1 />}
    </div>
  );
};
